import { Product } from '@eo-storefronts/eo-core'
import { useSnackbar } from 'notistack'
import eventProvider from '~/src/utils/EventUtils'
import useLogAddToCart from '~/src/hooks/analytics/ecommerce/useLogAddToCart'
import { useEoState, useEoValue, useSetEoState } from '~/src/hooks/useEoState'
import { useTranslations } from '~/src/hooks/useTranslations'
import { CART_MODIFIER_GROUP_STATE, CART_OF_FIRM_SELECTOR, CART_STATE, CartState } from '~/src/stores/cart'
import { FIRM_SELECTOR } from '~/src/stores/firm'
import { PRODUCT_COMMENT_STATE } from '~/src/stores/product-comment'
import { CartProduct } from '~/src/types/CartProduct'
import { getCartProductIndex } from '~/src/helpers/getCartProductIndex'
import { CHECKOUT_FORM_STATE, CheckoutFormState, COUPON_STATE } from '~/src/stores/checkout'
import useLoyaltyService from '~/src/hooks/customer/useLoyaltyService'
import { PRODUCTS_STATE } from '~/src/stores/product'
import useDialog from '~/src/hooks/useDialog'

interface ReturnsType {
  addToCart(product: Product, quantity: number): void,
}

const useAddProductToCart = (): ReturnsType => {
  const [ coupon, setCoupon ] = useEoState(COUPON_STATE)
  const setCarts = useSetEoState(CART_STATE)
  const setCheckoutForm = useSetEoState(CHECKOUT_FORM_STATE)
  const firm = useEoValue(FIRM_SELECTOR)
  const products = useEoValue(PRODUCTS_STATE)
  const modifierGroups = useEoValue(CART_MODIFIER_GROUP_STATE)
  const productComment = useEoValue(PRODUCT_COMMENT_STATE)
  const cart = useEoValue(CART_OF_FIRM_SELECTOR)
  const loyaltyService = useLoyaltyService()
  const { dismiss } = useDialog()
  const { enqueueSnackbar } = useSnackbar()
  const { log: logAddToCart } = useLogAddToCart()
  const { t } = useTranslations()

  const addToCart = (product: Product, quantity: number): void => {
    if (!firm) {
      return
    }

    const isCoupon = coupon?.product?.id === product.id || coupon?.category?.id === product.categoryId
    const isLoyalty = loyaltyService.isProductEligible(product) && !cart.find((cp: CartProduct) => cp.is_loyalty)

    setCarts((state: CartState) => {
      const partialCartProduct: Partial<CartProduct> = {
        id: product.id,
        comment: productComment,
        is_coupon: isCoupon,
        is_loyalty: isLoyalty,
        modifierGroups
      }

      const cartProducts: CartProduct[] = [
        ...state[firm.id] || []
      ]
      const sameItemFoundInCart: number = getCartProductIndex(cartProducts, partialCartProduct as CartProduct)

      if (sameItemFoundInCart > -1 && !isCoupon && !isLoyalty) {
        cartProducts[sameItemFoundInCart] = {
          ...cartProducts[sameItemFoundInCart],
          quantity: cartProducts[sameItemFoundInCart].quantity + quantity
        }
      } else {
        cartProducts.push({
          ...partialCartProduct,
          quantity
        } as CartProduct)
      }

      return {
        ...state,
        [firm.id]: cartProducts
      }
    })

    if (isLoyalty) {
      dismiss('loyalty-products-modal')
      setCheckoutForm((state: CheckoutFormState) => ({
        ...state,
        loyaltyDiscount: product.price,
        loyaltyProductId: product.id
      }))
    }

    if (isCoupon) {
      dismiss('coupon-products-modal')
      setCheckoutForm((state: CheckoutFormState) => ({
        ...state,
        coupon: {
          code: coupon.code,
          chosen_product_id: product.id || null,
          value: product.id ? products[product.id].price : (coupon.value || 0),
          type: coupon.type,
          product: coupon.product,
          category: coupon.category
        }
      }))
      setCoupon(null)
    }

    logAddToCart(
      {
        id: product.id,
        comment: productComment,
        modifierGroups,
        quantity,
        is_coupon: isCoupon,
        is_loyalty: isLoyalty
      }
    )

    enqueueSnackbar({
      message: t('product.added_to_cart'),
      variant: 'success'
    })

    eventProvider.trigger('add_to_cart')
  }

  return { addToCart }
}

export {
  useAddProductToCart
}
